/*
 * Swoveralls paid-traffic landing page.
 * - Shop module: color (separate products) + size (variants) selection, direct add to cart
 *   into the theme's cart drawer.
 * - Sticky mobile CTA that never adds an unselected or unavailable variant.
 * - In-page CTAs, muted demo video autoplay, and dataLayer events prefixed `swov_lp_`.
 *   Commerce events (add_to_cart, begin_checkout, purchase) are left to Elevar / Shopify
 *   so they aren't double counted.
 */
(function () {
  const EVENT_PREFIX = 'swov_lp_';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function pageVariant() {
    const shop = document.querySelector('swov-lp-shop');
    return shop ? shop.dataset.pageVariant : '';
  }

  function track(name, data) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: EVENT_PREFIX + name, lp_variant: pageVariant() }, data || {}));
  }

  function el(tag, attrs, children) {
    const node = document.createElement(tag);
    Object.entries(attrs || {}).forEach(([key, value]) => {
      if (value === false || value === null || value === undefined) return;
      if (key === 'text') node.textContent = value;
      else if (key === 'className') node.className = value;
      else node.setAttribute(key, value === true ? '' : value);
    });
    (children || []).forEach((child) => child && node.appendChild(child));
    return node;
  }

  function utmParams() {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
      if (params.get(key)) utm[key] = params.get(key);
    });
    return utm;
  }

  function scrollToShop() {
    const shop = document.getElementById('shop');
    if (!shop) return null;
    shop.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    shop.focus({ preventScroll: true });
    return shop;
  }

  class SwovLpShop extends HTMLElement {
    connectedCallback() {
      const productsScript = this.querySelector('[data-swov-lp-products]');
      if (!productsScript) return;

      this.products = JSON.parse(productsScript.textContent);
      this.strings = JSON.parse(this.querySelector('[data-swov-lp-shop-strings]').textContent);
      this.byHandle = Object.fromEntries(this.products.map((product) => [product.handle, product]));

      this.form = this.querySelector('[data-shop-form]');
      this.sizesEl = this.querySelector('[data-sizes]');
      this.sizeFieldset = this.querySelector('[data-size-fieldset]');
      this.track = this.querySelector('[data-gallery-track]');
      this.thumbs = this.querySelector('[data-gallery-thumbs]');
      this.atc = this.querySelector('[data-atc]');
      this.atcLabel = this.querySelector('[data-atc-label]');
      this.spinner = this.querySelector('.swov-lp-spinner');
      this.errorEl = this.querySelector('[data-error]');
      this.sticky = this.querySelector('[data-sticky]');
      this.stickyButton = this.querySelector('[data-sticky-button]');

      this.product = this.products[0];
      this.variant = null;
      this.engaged = false;
      this.busy = false;
      this.lastStoredSize = this.storedQuizSize();

      this.bindEvents();
      this.applyUrlSelection();
      this.renderThumbs();
      this.markQuizSize();
      this.updateButtons();
      this.observeVisibility();
    }

    /* ---------- setup ---------- */

    bindEvents() {
      this.addEventListener('change', (event) => {
        const input = event.target;
        if (input.name === `color-${this.dataset.sectionId}`) this.selectColor(input.value, { user: true });
        if (input.name === 'id') this.selectSize(Number(input.value), { user: true });
      });

      const moreToggle = this.querySelector('[data-more-toggle]');
      if (moreToggle) {
        moreToggle.addEventListener('click', () => {
          const panel = document.getElementById(moreToggle.getAttribute('aria-controls'));
          const expanded = moreToggle.getAttribute('aria-expanded') === 'true';
          moreToggle.setAttribute('aria-expanded', String(!expanded));
          panel.hidden = expanded;
          if (!expanded) track('more_colors_open');
        });
      }

      this.querySelector('[data-size-quiz]')?.addEventListener('click', () => this.openSizeHelp('quiz'));
      this.querySelector('[data-size-chart]')?.addEventListener('click', () => this.openSizeHelp('chart'));

      const quizModal = document.getElementById('size-quiz-modal');
      if (quizModal) quizModal.addEventListener('close', () => this.onQuizClosed());

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.addToCart('module');
      });

      if (this.stickyButton) {
        this.stickyButton.addEventListener('click', () => {
          if (this.variant && this.variant.available) {
            track('sticky_cta_click', { action: 'add_to_cart' });
            this.addToCart('sticky');
          } else {
            track('sticky_cta_click', { action: 'scroll' });
            scrollToShop();
            if (this.engaged) this.focusFirstSize();
          }
        });
      }

      if (this.track) {
        let frame;
        this.track.addEventListener('scroll', () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => this.updateActiveThumb());
        });
      }
    }

    applyUrlSelection() {
      const params = new URLSearchParams(window.location.search);
      const colorParam = (params.get('color') || '').toLowerCase().trim();
      if (colorParam) {
        const slug = colorParam.replace(/\s+/g, '-');
        const match =
          this.byHandle[colorParam] ||
          this.byHandle[`swoveralls-${slug}`] ||
          this.products.find((product) => product.color.toLowerCase() === colorParam);
        if (match && match !== this.product) this.selectColor(match.handle, { user: false });
      }

      const sizeParam = (params.get('size') || '').toUpperCase().trim();
      if (sizeParam) this.selectSizeByLabel(sizeParam, { user: false });
    }

    observeVisibility() {
      const moduleObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            if (!this.engaged) track('shop_module_view', { product_handle: this.product.handle });
            this.engaged = true;
            moduleObserver.disconnect();
            this.updateButtons();
          });
        },
        { threshold: 0.25 }
      );
      moduleObserver.observe(this.form);

      if (!this.sticky) return;
      this.visible = { hero: false, form: false };
      const heroCta = document.querySelector('[data-swov-lp-cta="hero"]');
      const stickyObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.visible[entry.target === this.form ? 'form' : 'hero'] = entry.isIntersecting;
        });
        this.updateSticky();
      });
      stickyObserver.observe(this.form);
      if (heroCta) stickyObserver.observe(heroCta);
    }

    /* ---------- selection ---------- */

    selectColor(handle, { user } = {}) {
      const product = this.byHandle[handle];
      if (!product) return;
      const previousSize = this.variant ? this.variant.size : null;

      this.product = product;
      this.variant = null;
      this.clearError();

      const radio = this.querySelector(`input[name="color-${this.dataset.sectionId}"][value="${CSS.escape(handle)}"]`);
      if (radio) {
        radio.checked = true;
        const morePanel = radio.closest('[data-swatches-more]');
        if (morePanel && morePanel.hidden) this.querySelector('[data-more-toggle]')?.click();
      }

      this.querySelector('[data-color-name]').textContent = product.color;
      this.querySelector('[data-limited-badge]').hidden = !product.limited;
      this.querySelector('[data-product-title]').textContent = product.title;
      this.renderPrice();
      this.renderRating();
      this.renderGallery();
      this.renderSizes();
      if (previousSize) this.selectSizeByLabel(previousSize, { user: false });
      this.markQuizSize();
      this.updateButtons();

      if (user) {
        track('color_select', {
          product_handle: product.handle,
          color: product.color,
          featured_color: product.featured,
        });
      }
    }

    selectSize(variantId, { user } = {}) {
      const variant = this.product.variants.find((v) => v.id === variantId);
      if (!variant || !variant.available) return;
      this.variant = variant;
      const input = this.sizesEl.querySelector(`input[value="${variant.id}"]`);
      if (input) input.checked = true;
      this.querySelector('[data-size-name]').textContent = variant.size;
      this.clearError();
      this.renderPrice();
      this.updateButtons();
      if (user) track('size_select', { product_handle: this.product.handle, size: variant.size, variant_id: variant.id });
    }

    selectSizeByLabel(label, options) {
      const variant = this.product.variants.find((v) => v.size.toUpperCase() === label.toUpperCase());
      if (variant && variant.available) this.selectSize(variant.id, options);
    }

    /* ---------- size help ---------- */

    openSizeHelp(tab) {
      track('size_guide_open', { tab });
      if (window.SwoverallsSizeQuiz) {
        window.SwoverallsSizeQuiz.open({ tab });
      } else {
        window.location.href = this.product.url;
      }
    }

    storedQuizSize() {
      return window.SwoverallsSizeQuiz ? window.SwoverallsSizeQuiz.getStoredSize() : this.readStoredSize();
    }

    readStoredSize() {
      try {
        return localStorage.getItem('swoveralls_recommended_size');
      } catch (e) {
        return null;
      }
    }

    onQuizClosed() {
      const size = this.storedQuizSize();
      if (size && size !== this.lastStoredSize) {
        this.selectSizeByLabel(size, { user: false });
        if (this.variant && this.variant.size === size) track('size_select', { size, source: 'size_quiz' });
      }
      this.lastStoredSize = size;
      this.markQuizSize();
    }

    markQuizSize() {
      const size = this.storedQuizSize();
      this.sizesEl.querySelectorAll('.swov-lp-size').forEach((label) => {
        const isPick = Boolean(size) && label.dataset.size === size;
        label.classList.toggle('is-recommended', isPick);
        const badge = label.querySelector('.swov-lp-size__pick');
        if (isPick && !badge) {
          label.appendChild(el('span', { className: 'swov-lp-size__pick', text: this.strings.quizSize }));
        } else if (!isPick && badge) {
          badge.remove();
        }
      });
    }

    /* ---------- rendering ---------- */

    renderPrice() {
      const source = this.variant || this.product;
      this.querySelector('[data-price]').textContent = source.price;
      const compare = this.querySelector('[data-compare]');
      compare.textContent = source.compare || '';
      compare.hidden = !source.compare;
    }

    renderRating() {
      const rating = this.querySelector('[data-rating]');
      const { rating: value, ratingCount } = this.product;
      rating.hidden = !(value && ratingCount > 0);
      if (rating.hidden) return;
      const rounded = Math.round(value * 10) / 10;
      rating.replaceChildren(
        el('span', { className: 'swov-lp-stars', style: `--swov-lp-stars: ${Math.round(value * 20)}%`, 'aria-hidden': 'true', text: '★★★★★' }),
        el('span', { className: 'visually-hidden', text: `${rounded} out of 5 stars` }),
        el('span', { text: `${rounded} ` }, [
          el('span', { className: 'swov-lp-rating__count', text: `(${ratingCount} reviews)` }),
        ])
      );
    }

    renderSizes() {
      const labels = this.product.variants.map((variant) => {
        const input = el('input', {
          type: 'radio',
          name: 'id',
          value: variant.id,
          className: 'visually-hidden',
          disabled: !variant.available,
          required: true,
        });
        return el(
          'label',
          { className: `swov-lp-size${variant.available ? '' : ' is-unavailable'}`, 'data-size': variant.size },
          [
            input,
            el('span', { text: variant.size }),
            variant.available ? null : el('span', { className: 'visually-hidden', text: ` (${this.strings.soldOut})` }),
          ]
        );
      });
      this.sizesEl.replaceChildren(...labels);
      this.querySelector('[data-size-name]').textContent = this.strings.sizePrompt;
    }

    renderGallery() {
      const media = this.product.media;
      const slides = media.map((item, index) =>
        el('figure', { className: 'swov-lp-gallery__slide', role: 'group', 'aria-label': `${index + 1} of ${media.length}` }, [
          el('img', {
            src: item.src,
            srcset: item.srcset,
            sizes: '(min-width: 990px) 560px, 100vw',
            alt: item.alt,
            width: item.width,
            height: item.height,
            loading: index === 0 ? 'eager' : 'lazy',
          }),
        ])
      );
      this.track.replaceChildren(...slides);
      this.track.scrollLeft = 0;
      this.renderThumbs();
    }

    renderThumbs() {
      if (!this.thumbs) return;
      const buttons = this.product.media.map((item, index) => {
        const button = el('button', {
          type: 'button',
          className: 'swov-lp-gallery__thumb',
          'aria-label': `Show image ${index + 1}`,
          'aria-current': index === 0 ? 'true' : false,
        }, [el('img', { src: item.thumb, alt: '', width: 64, height: 80, loading: 'lazy' })]);
        button.addEventListener('click', () => {
          const slide = this.track.children[index];
          if (!slide) return;
          this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        });
        return button;
      });
      this.thumbs.replaceChildren(...buttons);
    }

    updateActiveThumb() {
      const index = Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
      Array.from(this.thumbs.children).forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }

    updateButtons() {
      const soldOut = !this.product.available;
      this.atc.disabled = soldOut || this.busy;
      this.atc.setAttribute('aria-busy', String(this.busy));
      if (soldOut) this.atcLabel.textContent = this.strings.soldOut;
      else if (this.variant) this.atcLabel.textContent = `${this.strings.addToCart} — ${this.variant.price}`;
      else this.atcLabel.textContent = this.strings.addToCart;
      this.updateSticky();
    }

    updateSticky() {
      if (!this.sticky) return;
      const show = this.visible && !this.visible.hero && !this.visible.form;
      this.sticky.hidden = !show;
      document.body.classList.toggle('swov-lp-has-sticky', Boolean(show));

      this.querySelector('[data-sticky-name]').textContent = this.product.color;
      this.querySelector('[data-sticky-price]').textContent = (this.variant || this.product).price;
      if (this.variant && this.variant.available) {
        this.stickyButton.textContent = `${this.strings.addToCart} — ${this.variant.price}`;
      } else {
        this.stickyButton.textContent = this.engaged ? this.strings.chooseSize : this.strings.findPair;
      }
      this.stickyButton.disabled = this.busy;
    }

    /* ---------- cart ---------- */

    focusFirstSize() {
      const first = this.sizesEl.querySelector('input:not(:disabled)');
      if (first) first.focus({ preventScroll: true });
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    async addToCart(source) {
      if (this.busy) return;
      if (!this.product.available) return;
      if (!this.variant || !this.variant.available) {
        this.showError(this.strings.pickSize);
        if (source === 'sticky') scrollToShop();
        this.sizeFieldset.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
        this.focusFirstSize();
        return;
      }

      const variant = this.variant;
      const cartDrawer = document.querySelector('cart-drawer');
      const body = { items: [{ id: variant.id, quantity: 1 }] };
      if (cartDrawer) {
        body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        cartDrawer.setActiveElement(document.activeElement);
      }

      this.busy = true;
      this.spinner.hidden = false;
      this.clearError();
      this.updateButtons();

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, { source: 'swov-lp-shop', productVariantId: variant.id, errors: data.description || data.message, message: data.message });
          }
          this.showError(data.description || data.message || this.strings.genericError);
          return;
        }

        this.saveCartAttributes();

        if (!cartDrawer) {
          window.location = window.routes.cart_url;
          return;
        }
        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'swov-lp-shop', productVariantId: variant.id, cartData: data });
        }
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(data);
      } catch (error) {
        console.error(error);
        this.showError(this.strings.genericError);
      } finally {
        this.busy = false;
        this.spinner.hidden = true;
        this.updateButtons();
      }
    }

    // Underscore-prefixed attributes stay hidden from shoppers but are kept on the order,
    // so orders can be attributed to this page and variant.
    saveCartAttributes() {
      const attributes = { _lp_variant: this.dataset.pageVariant, _lp_path: window.location.pathname };
      Object.entries(utmParams()).forEach(([key, value]) => {
        attributes[`_lp_${key}`] = value;
      });
      fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      }).catch(() => {});
    }
  }

  if (!customElements.get('swov-lp-shop')) customElements.define('swov-lp-shop', SwovLpShop);

  /* ---------- page-level behavior ---------- */

  document.addEventListener('click', (event) => {
    const cta = event.target.closest('[data-swov-lp-cta]');
    if (!cta || !document.getElementById('shop')) return;
    event.preventDefault();
    const location = cta.dataset.swovLpCta;
    track(location === 'hero' ? 'hero_cta_click' : 'cta_click', { cta_location: location });
    scrollToShop();
  });

  function openCustomerReviews({ scroll } = {}) {
    const section = document.querySelector('[data-swov-lp-review-list]');
    const panel = section && section.querySelector('[data-review-panel]');
    if (!panel) return false;
    const wasHidden = panel.hidden;
    panel.hidden = false;
    document.querySelectorAll('[data-swov-lp-reviews-toggle]').forEach((toggle) => toggle.setAttribute('aria-expanded', 'true'));
    if (wasHidden) track('reviews_expand');
    if (scroll) {
      section.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      const heading = panel.querySelector('h2');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
    }
    return true;
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href="#customer-reviews"]');
    if (!link || !openCustomerReviews({ scroll: true })) return;
    event.preventDefault();
  });

  function initVideos() {
    const figures = document.querySelectorAll('[data-swov-lp-autoplay]');
    if (!figures.length || reducedMotion.matches) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('video');
          if (!video) return;
          if (entry.isIntersecting) {
            video.muted = true;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );
    figures.forEach((figure) => observer.observe(figure));
  }

  function init() {
    track('landing_page_view', Object.assign({ page_path: window.location.pathname }, utmParams()));
    initVideos();
    if (window.location.hash === '#customer-reviews') openCustomerReviews({ scroll: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
